/**
 * Phase 20 — Guided estimate wizard shell (web).
 *
 * A presentation layer over the existing Stella chat. It renders one
 * step at a time (intent → dimensions → finish → photos → estimate) with
 * a progress header and Back/Continue, and drives each step by firing the
 * SAME synthetic chat turn the inline chat fires today — via the
 * `ChatPanelHandle` ref. The chat itself stays mounted (hidden in guided
 * mode) so there is exactly one message stream; "Ask Stella" reveals it
 * as an escape hatch on every step (FR-006/FR-014).
 *
 * No backend/pricing/prompt change: the estimate produced here is the
 * same one the equivalent chat sequence produces (SC-004).
 */

import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";

import type {
  ChatMessage,
  EstimateStreamPayload,
  OptionsCardPayload,
  PackageAddOn,
} from "@starz/shared/api/types.gen";
import { buildSelectedMaterialTurn } from "@starz/shared/chat/directives";
import {
  applyPackage,
  ensureRoomType,
  isOtherIntent,
  isOtherProjectType,
  usePackages,
  useEstimateWizard,
  type DimensionSummaryItem,
} from "@starz/shared/wizard";

import type { ChatPanelHandle } from "@/components/chat/ChatPanel";
import { WizardProgress } from "./WizardProgress";
import { IntentStep } from "./steps/IntentStep";
import { ProjectTypeStep } from "./steps/ProjectTypeStep";
import { DimensionsStep, type WizardMeasurement } from "./steps/DimensionsStep";
import { FinishStep } from "./steps/FinishStep";
import { PhotosStep } from "./steps/PhotosStep";
import { EstimateStep } from "./steps/EstimateStep";

/** Turn a flexible measurement into a context-carrying turn the Phase 20
 * synthesizer routes to the right axis (wall / floor / linear). */
function measurementSentence(space: string, m: WizardMeasurement): string {
  if (m.kind === "wall") {
    return `The ${space} wall is ${m.widthFt} ft wide by ${m.heightFt} ft tall.`;
  }
  if (m.kind === "floor") {
    return `The ${space} floor is ${m.areaSqft} square feet.`;
  }
  return `The ${space} is ${m.lengthFt} ft (linear run).`;
}

interface WizardSignalProps {
  hasIntent: boolean;
  hasDimensions: boolean;
  hasFinish: boolean;
  hasPhotos: boolean;
  hasEstimate: boolean;
}

interface Props extends WizardSignalProps {
  estimateId: string;
  /** Shared ref to the underlying ChatPanel — the wizard drives turns through it. */
  chatRef: RefObject<ChatPanelHandle | null>;
  /** Renders the ChatPanel with the wizard's `onConversationChange` injected. */
  renderChat: (injected: {
    onConversationChange: (state: { messages: ChatMessage[]; streaming: boolean }) => void;
  }) => ReactNode;
  /** Admin/read-only viewer — render the chat only, no wizard controls (FR-013). */
  readOnly?: boolean;
  /** Human label for the space (for dimension read-back copy). */
  spaceLabel?: string | null;
  /** Photo count from the workspace (photos step completion + copy). */
  photoCount: number;
  /** Wrapped snapshot payload for the estimate step. */
  snapshotPayload: EstimateStreamPayload | null;
  /** Captured measurements (e.g. from a room scan) shown on the dimensions
   * step so the customer can confirm and continue instead of re-entering. */
  capturedDimensions?: DimensionSummaryItem[];
  /** Called after the photos step uploads, so the host updates photo state. */
  onPhotoUploaded: (photo: import("@starz/shared/api/types.gen").Photo) => void;
  /** Fired when a chat turn settles (streaming → idle) so the host can
   * refetch estimate signals — this is how a measurement Stella captured
   * in the escape hatch flows back and completes the current step. */
  onTurnSettled?: () => void;
}

export function EstimateWizard({
  estimateId,
  chatRef,
  renderChat,
  readOnly = false,
  spaceLabel,
  photoCount,
  snapshotPayload,
  capturedDimensions = [],
  onPhotoUploaded,
  hasIntent,
  hasDimensions,
  hasFinish,
  hasPhotos,
  hasEstimate,
  onTurnSettled,
}: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [viewMode, setViewMode] = useState<"guided" | "chat">("guided");
  const [photosSkipped, setPhotosSkipped] = useState(false);
  const [selectedFinishId, setSelectedFinishId] = useState<string | null>(null);
  // Read-back for the dimensions step when the customer navigates Back.
  const [lastMeasurement, setLastMeasurement] = useState<WizardMeasurement | null>(null);
  // Phase 21 — a package apply is in flight / last one failed, plus the
  // add-ons the applied package offers (deliberately not in the total).
  const [applyingPackage, setApplyingPackage] = useState(false);
  const [packageError, setPackageError] = useState<string | null>(null);
  const [packageAddOns, setPackageAddOns] = useState<PackageAddOn[]>([]);
  const [addedAddOnIds, setAddedAddOnIds] = useState<string[]>([]);

  // Phase 21 — the project-type choices for this estimate. The endpoint
  // scopes by the estimate's room type, so an empty list is the signal to
  // show no step at all (FR-018) — no room-type check needed here.
  const {
    packages,
    appliedPackageId,
    hasProjectTypes,
    refetch: refetchPackages,
  } = usePackages(readOnly ? null : estimateId);

  const wizard = useEstimateWizard({
    hasIntent,
    hasDimensions,
    hasFinish,
    hasPhotos,
    hasEstimate,
    photosSkipped,
    hasProjectTypes,
    hasProjectType: appliedPackageId != null,
    streaming,
    readOnly,
  });

  // When a turn settles (streaming → idle): advance if a step fired the
  // turn, and always notify the host to refetch signals — so a
  // measurement/selection Stella captured (even via the escape hatch)
  // flows back and completes the relevant step.
  const pendingAdvanceRef = useRef(false);
  const prevStreamingRef = useRef(false);
  useEffect(() => {
    if (prevStreamingRef.current && !streaming) {
      if (pendingAdvanceRef.current) {
        pendingAdvanceRef.current = false;
        wizard.next();
      }
      onTurnSettled?.();
      // Phase 21 — the estimate's room_type is set DURING the intent
      // turn (by Stella's IDENTITY directive, or by our own stamp), but
      // the packages list was fetched at mount when it was still null.
      // Without re-reading here the project-type step stays hidden for
      // the whole session and only appears after a reload.
      refetchPackages();
    }
    prevStreamingRef.current = streaming;
  }, [streaming, wizard, onTurnSettled, refetchPackages]);

  const chatEl = useMemo(
    () =>
      renderChat({
        onConversationChange: ({ messages: m, streaming: s }) => {
          setMessages(m);
          setStreaming(s);
        },
      }),
    [renderChat],
  );

  // Latest intent-picker payload (Stella's kickoff cards) and latest
  // material options payload, pulled from the live conversation.
  const intentPayload = useMemo<OptionsCardPayload | null>(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].kind === "intent_picker" && messages[i].options_payload) {
        return messages[i].options_payload as OptionsCardPayload;
      }
    }
    return null;
  }, [messages]);

  const optionsPayload = useMemo<OptionsCardPayload | null>(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.kind !== "intent_picker" && m.options_payload) {
        return m.options_payload as OptionsCardPayload;
      }
    }
    return null;
  }, [messages]);

  // "Other" intent: after the customer describes their project in chat and
  // Stella captures it (room_type set → hasIntent), return to the wizard
  // and continue to the next step.
  const awaitingOtherRef = useRef(false);
  useEffect(() => {
    if (awaitingOtherRef.current && hasIntent) {
      awaitingOtherRef.current = false;
      setViewMode("guided");
      wizard.goTo("dimensions");
    }
  }, [hasIntent, wizard]);

  // Read-only viewers (admin) get the plain chat — no wizard chrome.
  if (readOnly) {
    return <>{chatEl}</>;
  }

  const markPendingAdvance = () => {
    pendingAdvanceRef.current = true;
  };

  // Intent selection. The "Other" catch-all hands off to Stella to gather a
  // free-text description; every other intent fires the normal turn and
  // advances.
  const onSelectIntent = (id: string, name: string) => {
    if (isOtherIntent(id, name)) {
      awaitingOtherRef.current = true;
      setViewMode("chat");
      void chatRef.current?.sendText(
        "I'd like help with a project that isn't in the list — let me describe it.",
      );
      return;
    }
    // Phase 21 — say what the customer chose rather than hoping Stella
    // emits `[[IDENTITY:room_type=…]]`. Without this the bathroom
    // project-type step can never appear (see ensureRoomType). Fire and
    // forget: it must not delay or block the intent turn, and the
    // packages refetch below picks the result up either way.
    const card = intentPayload?.cards.find((c) => c.option_id === id);
    void ensureRoomType(estimateId, card?.default_room_type).then((wrote) => {
      if (wrote) refetchPackages();
    });
    markPendingAdvance();
    void chatRef.current?.selectIntent(id, name);
  };

  // Phase 21 — project-type selection.
  //
  // This step does NOT fire a chat turn, so it cannot use the
  // `streaming → idle` settle hook the other steps advance on. It awaits
  // the apply, refetches the host's signals, and advances itself.
  const onSelectProjectType = async (packageId: string) => {
    if (isOtherProjectType(packageId)) {
      // Genuinely bespoke work goes to Stella rather than being squeezed
      // into a bundle — the same handoff the "Other" intent uses.
      setPackageError(null);
      setViewMode("chat");
      void chatRef.current?.sendText(
        "My bathroom project isn't one of the listed options — let me describe it.",
      );
      return;
    }
    setApplyingPackage(true);
    setPackageError(null);
    const outcome = await applyPackage(estimateId, packageId);
    setApplyingPackage(false);
    if (!outcome.ok) {
      setPackageError(outcome.errorMessage ?? "Something went wrong.");
      return;
    }
    // Offer this package's add-ons on the estimate step. Switching
    // packages replaces them rather than accumulating.
    setPackageAddOns(outcome.result?.add_ons ?? []);
    setAddedAddOnIds([]);
    // Reflect the new selections + applied package, then move on. The
    // dimensions step is next and will ask for a measurement only if the
    // package actually needs one.
    refetchPackages();
    onTurnSettled?.();
    wizard.next();
  };

  const currentStep = wizard.currentStepId;

  function renderStepBody(): ReactNode {
    switch (currentStep) {
      case "intent":
        return (
          <IntentStep
            payload={intentPayload}
            disabled={streaming}
            onSelectIntent={onSelectIntent}
            onSendText={(text) => {
              markPendingAdvance();
              void chatRef.current?.sendText(text);
            }}
          />
        );
      case "project_type":
        return (
          <ProjectTypeStep
            packages={packages}
            appliedPackageId={appliedPackageId}
            busy={applyingPackage}
            errorMessage={packageError}
            disabled={streaming}
            onSelect={(packageId) => void onSelectProjectType(packageId)}
          />
        );
      case "dimensions":
        return (
          <DimensionsStep
            spaceLabel={spaceLabel}
            captured={capturedDimensions}
            initial={lastMeasurement}
            disabled={streaming}
            onSubmit={(m) => {
              setLastMeasurement(m);
              markPendingAdvance();
              const space = spaceLabel ? spaceLabel : "space";
              void chatRef.current?.sendText(measurementSentence(space, m));
            }}
            onMeasureWithStella={() => {
              // Hand off to Stella with context (the wizard → Stella
              // direction): reveal the chat and ask to measure. Pin the
              // wizard to the dimensions step so that when the scan
              // completes, the customer returns HERE to see the captured
              // measurements and confirm — rather than silently skipping
              // ahead. onTurnSettled refetches the values.
              wizard.goTo("dimensions");
              setViewMode("chat");
              void chatRef.current?.askForMeasurement();
            }}
          />
        );
      case "finish":
        return (
          <FinishStep
            payload={optionsPayload}
            selectedOptionId={selectedFinishId}
            streaming={streaming}
            onRequestOptions={() => void chatRef.current?.askForOptions()}
            onChooseWithStella={() => setViewMode("chat")}
            onSelect={(card) => {
              setSelectedFinishId(card.option_id);
              markPendingAdvance();
              void chatRef.current?.sendText(
                buildSelectedMaterialTurn({
                  optionGroupId: card.option_group_id ?? optionsPayload?.group_id ?? "",
                  optionId: card.option_id,
                  displayName: card.display_name,
                }),
              );
            }}
          />
        );
      case "photos":
        return (
          <PhotosStep
            estimateId={estimateId}
            photoCount={photoCount}
            disabled={streaming}
            onUploaded={onPhotoUploaded}
            onSkip={() => {
              setPhotosSkipped(true);
              wizard.next();
            }}
          />
        );
      case "estimate":
        return (
          <EstimateStep
            snapshotPayload={snapshotPayload}
            streaming={streaming}
            onRequestEstimate={() => void chatRef.current?.askForEstimate()}
            addOns={packageAddOns}
            selectedMaterialIds={addedAddOnIds}
            onAddAddOn={(addOn) => {
              // Goes through the ordinary option picker, so it lands in
              // its own slot and behaves like any other selection.
              setAddedAddOnIds((ids) => [...ids, addOn.material_id]);
              void chatRef.current?.sendText(
                buildSelectedMaterialTurn({
                  optionGroupId: addOn.option_group_id ?? "",
                  optionId: addOn.material_id,
                  displayName: addOn.display_name,
                }),
              );
            }}
          />
        );
    }
  }

  const stepTitle = wizard.steps[wizard.currentIndex]?.title ?? "";

  // "Ask Stella" escape-hatch view: reveal the full chat over the same
  // stream; a control returns to the guided steps (state recomputed).
  if (viewMode === "chat") {
    return (
      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={() => {
            // Refetch signals on the way back so anything captured in the
            // chat (a room scan, a selection) is reflected in the wizard.
            onTurnSettled?.();
            setViewMode("guided");
          }}
          className="self-start rounded-full border border-starz-gold/30 px-4 py-2 text-sm font-semibold text-starz-navy hover:bg-starz-cream"
        >
          ← Back to guided steps
        </button>
        {chatEl}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-starz-gold/15 bg-white shadow-sm">
      {/* Header */}
      <div className="bg-starz-navy px-6 py-5 text-starz-off-white">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-starz-gold">
          Starz Remodeling
        </p>
        <h2 className="mt-1 font-serif text-xl">{stepTitle}</h2>
        <div className="mt-4">
          <WizardProgress
            steps={wizard.steps}
            currentIndex={wizard.currentIndex}
            currentStepId={wizard.currentStepId}
            completed={wizard.completed}
          />
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-6">{renderStepBody()}</div>

      {/* Ask-Stella escape hatch (present on every step) */}
      <div className="px-6 pb-2 text-center">
        <button
          type="button"
          onClick={() => setViewMode("chat")}
          className="text-sm font-semibold text-starz-gold hover:underline"
        >
          💬 Ask Stella a question
        </button>
      </div>

      {/* Footer navigation */}
      <div className="flex items-center gap-3 border-t border-starz-gold/15 bg-starz-cream px-6 py-4">
        <button
          type="button"
          onClick={() => wizard.back()}
          disabled={!wizard.canGoBack}
          className="rounded-lg px-4 py-3 text-sm font-semibold text-warm-gray disabled:invisible"
        >
          Back
        </button>
        <div className="flex-1" />
        {wizard.canAdvance ? (
          <button
            type="button"
            onClick={() => wizard.next()}
            className="min-h-[44px] rounded-lg bg-starz-navy px-6 py-3 text-sm font-semibold uppercase tracking-wide text-starz-off-white hover:bg-starz-navy/90"
          >
            Continue
          </button>
        ) : null}
      </div>
    </div>
  );
}
